// Transaction category/type display metadata for the UI logEvent feed.
//
// Uses lazy translation: until i18n is initialized, return the untranslated
// base values. After init, translate once and cache.
import type { LogEventType } from "../../../../common/types.ts";
import i18n from "./i18n.ts";

const categoriesBase = {
	award: {
		text: "Awards",
		className: "bg-warning",
	},
	draft: {
		text: "Draft",
		className: "bg-darkblue",
	},
	league: {
		text: "League",
		className: "bg-secondary",
	},
	injury: {
		text: "Injuries",
		className: "bg-danger",
	},
	playerFeat: {
		text: "Player Highlights",
		className: "bg-info",
	},
	playoffs: {
		text: "Playoffs",
		className: "bg-orange",
	},
	rare: {
		text: "Rare Events",
		className: "bg-dark",
	},
	transaction: {
		text: "Transactions",
		className: "bg-success",
	},
	team: {
		text: "Teams",
		className: "bg-light",
	},
};

const typesBase: Partial<
	Record<
		LogEventType,
		{
			text: string;
			category: keyof typeof categoriesBase;
		}
	>
> = {
	injured: {
		text: "Injury",
		category: "injury",
	},
	healed: {
		text: "Recovery",
		category: "injury",
	},
	playerFeat: {
		text: "Player Highlight",
		category: "playerFeat",
	},
	playoffs: {
		text: "Playoffs",
		category: "playoffs",
	},
	madePlayoffs: {
		text: "Playoffs",
		category: "playoffs",
	},
	freeAgent: {
		text: "Free Agent",
		category: "transaction",
	},
	reSigned: {
		text: "Re-signing",
		category: "transaction",
	},
	release: {
		text: "Released",
		category: "transaction",
	},
	retired: {
		text: "Retirement",
		category: "transaction",
	},
	trade: {
		text: "Trade",
		category: "transaction",
	},
	award: {
		text: "Award",
		category: "award",
	},
	hallOfFame: {
		text: "Hall of Fame",
		category: "award",
	},
	retiredJersey: {
		text: "Jersey Retirement",
		category: "award",
	},
	ageFraud: {
		text: "Fraud",
		category: "rare",
	},
	sisyphusTeam: {
		text: "Sisyphus Mode",
		category: "rare",
	},
	tragedy: {
		text: "Tragic Death",
		category: "rare",
	},
	teamContraction: {
		text: "Contraction",
		category: "team",
	},
	teamExpansion: {
		text: "Expansion",
		category: "team",
	},
	teamLogo: {
		text: "New Logo",
		category: "team",
	},
	teamRelocation: {
		text: "Relocation",
		category: "team",
	},
	teamRename: {
		text: "Rename",
		category: "team",
	},
	gameAttribute: {
		text: "League",
		category: "league",
	},
	draft: {
		text: "Draft",
		category: "draft",
	},
	draftLottery: {
		text: "Draft Lottery",
		category: "draft",
	},
	newLeague: {
		text: "New League",
		category: "league",
	},
	luxuryTax: {
		text: "Luxury Tax",
		category: "team",
	},
	luxuryTaxDist: {
		text: "Luxury Tax Dist",
		category: "team",
	},
	minPayroll: {
		text: "Minimum Payroll",
		category: "team",
	},
};

const ns = "ui";

let cachedCategories: typeof categoriesBase | null = null;
let cachedTypes: typeof typesBase | null = null;

const buildCategories = (): typeof categoriesBase =>
	Object.fromEntries(
		Object.entries(categoriesBase).map(([key, val]) => [
			key,
			{
				...val,
				text: i18n.t(`transactionInfo.categories.${key}`, {
					ns,
					defaultValue: val.text,
				}),
			},
		]),
	) as typeof categoriesBase;

const buildTypes = (): typeof typesBase =>
	Object.fromEntries(
		Object.entries(typesBase).map(([key, val]) => [
			key,
			val
				? {
						...val,
						text: i18n.t(`transactionInfo.types.${key}`, {
							ns,
							defaultValue: val.text,
						}),
					}
				: val,
		]),
	) as typeof typesBase;

export const categories = new Proxy(categoriesBase, {
	get(target, key) {
		if (!i18n.isInitialized) {
			return target[key as keyof typeof target];
		}
		if (!cachedCategories) {
			cachedCategories = buildCategories();
		}
		return cachedCategories[key as keyof typeof cachedCategories];
	},
});

export const types = new Proxy(typesBase, {
	get(target, key) {
		if (!i18n.isInitialized) {
			return target[key as keyof typeof target];
		}
		if (!cachedTypes) {
			cachedTypes = buildTypes();
		}
		return cachedTypes[key as keyof typeof cachedTypes];
	},
});
